import type { RtlContractKey } from '../hooks/useRtlTimeSeries';
import { contractKeyFromId } from '../hooks/useRtlTimeSeries';
import type { ScannerData } from '../types/ScannerData';
import { formatDate, parseDateInputToIso } from './dateUtils';
import { getEasternDateYmd } from './marketSession';
import { legacyUnderlyingSelectionMatches } from './filterUtils';

export const MAX_RTL_CHART_SERIES = 20;

export function mergeContractKeys(lists: RtlContractKey[][]): RtlContractKey[] {
  const map = new Map<string, RtlContractKey>();
  for (const list of lists) {
    for (const c of list) {
      map.set(c.id, c);
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => a.strike - b.strike || a.expiry.localeCompare(b.expiry) || a.optionSide.localeCompare(b.optionSide),
  );
}

export function contractKeyFromManual(
  symbol: string,
  strike: number,
  expiryYmd: string,
  optionSide: 'C' | 'P',
): RtlContractKey | null {
  const underlying = symbol.trim().toUpperCase();
  const exp = parseDateInputToIso(expiryYmd) || formatDate(expiryYmd);
  if (!underlying || !Number.isFinite(strike) || !exp) return null;
  const id = `${underlying}|${strike}|${exp}|${optionSide}`;
  return contractKeyFromId(id);
}

export function isExpiredExpiryYmd(expiryYmd: string): boolean {
  const ymd = formatDate(expiryYmd) || expiryYmd.slice(0, 10);
  if (!ymd) return false;
  return ymd < getEasternDateYmd();
}

function contractIdFromRow(row: ScannerData): string | null {
  const underlying = String(row.Underlying || '').trim().toUpperCase();
  const strike = Number(row.Strike);
  const expiryRaw = String(row['Expiry Date'] || '').slice(0, 10);
  const sideRaw = String(row['Option Type'] || 'C').toUpperCase();
  const optionSide = sideRaw.startsWith('P') ? 'P' : 'C';
  if (!underlying || !Number.isFinite(strike) || !expiryRaw) return null;
  return `${underlying}|${strike}|${expiryRaw}|${optionSide}`;
}

export function contractsFromScannerRows(
  rows: ScannerData[],
  symbol?: string,
): RtlContractKey[] {
  const sym = symbol?.trim().toUpperCase();
  const map = new Map<string, RtlContractKey>();
  for (const row of rows) {
    const id = contractIdFromRow(row);
    if (!id) continue;
    const key = contractKeyFromId(id);
    if (!key) continue;
    if (sym && !legacyUnderlyingSelectionMatches(key.underlying, [sym])) continue;
    map.set(id, key);
  }
  return Array.from(map.values()).sort(
    (a, b) => a.strike - b.strike || a.expiry.localeCompare(b.expiry) || a.optionSide.localeCompare(b.optionSide),
  );
}

export function expiryYmdForContract(c: RtlContractKey): string {
  return formatDate(c.expiry) || c.expiry.slice(0, 10);
}

export function listContractExpiries(contracts: RtlContractKey[]): string[] {
  const set = new Set<string>();
  for (const c of contracts) {
    const ymd = expiryYmdForContract(c);
    if (ymd) set.add(ymd);
  }
  return Array.from(set).sort();
}

export interface ContractChainRow {
  strike: number;
  call?: RtlContractKey;
  put?: RtlContractKey;
}

export function chainRowsForExpiry(contracts: RtlContractKey[], expiryYmd: string): ContractChainRow[] {
  const filtered = contracts.filter((c) => expiryYmdForContract(c) === expiryYmd);
  const byStrike = new Map<number, ContractChainRow>();
  for (const c of filtered) {
    const row = byStrike.get(c.strike) || { strike: c.strike };
    if (c.optionSide === 'C') row.call = c;
    else row.put = c;
    byStrike.set(c.strike, row);
  }
  return Array.from(byStrike.values()).sort((a, b) => a.strike - b.strike);
}

export function historicalParamsFromContractKey(key: RtlContractKey) {
  return {
    underlying: key.underlying.replace(/^\$/, '').replace(/\.X$/i, '') || key.underlying,
    strike: key.strike,
    expiry: expiryYmdForContract(key),
    optionType: key.optionSide === 'P' ? 'put' as const : 'call' as const,
    source: 'volume' as const,
  };
}
